import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UserMediaAppService } from '../../../shared/services';
import { DisplayListService } from '../../../displaylist/services';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FolderItemFilterModel } from '../../../displaylist/models';
import { NzDrawerComponent } from 'ng-zorro-antd/drawer';
import { AppUtils } from '../../../../helpers';
import { SharedModule } from '../../../shared/shared.module';
import { DisplayListMultiSelectComponent } from '../../../shared/components/display-list/multi-select/display-list-multi-select.component';
import { MediaListMultiSelectComponent } from '../../../shared/components/media/multi-select/media-list-multi-select.component';
import { SupportTimeOptions } from 'ng-zorro-antd/date-picker';
import { PlanAccessService } from '../../../../services';

@Component({
    standalone: true,
    imports: [SharedModule, DisplayListMultiSelectComponent, MediaListMultiSelectComponent],
  selector: 'app-event-drawer',
  templateUrl: './event-drawer.component.html',
  styleUrls: ['./event-drawer.component.scss']
})
export class EventDrawerComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  
  @Input() visible: boolean = false;
  @Input() availableContent: any[] = [];
  @Input() contentTypeList: any[] = [{id: 'media', name: 'Media'}, {id: 'playlist', name: 'Playlist'}];
  @Input() schedulerId: number = 0;
  @Input() userId: string = '';
  @Input() defaultTimeZone: string = 'UTC';
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  eventForm!: FormGroup;
  isEditMode: boolean = false;
  isSaving: boolean = false;
  
  // Content management
  filteredContent: any[] = [];
  selectedContentType: string = 'media';
  isLoadingContent: boolean = false;
  contentSearchText: string = '';
  
  // Pagination
  currentPage: number = 1;
  pageSize: number = 20;
  totalContent: number = 0;
  hasMore: boolean = true;

  // Content drawer management
  showMedias: boolean = false;
  selectedMedia: any[] = [];
  selectedMediaIds: number[] = [];
  selectedPlaylists: any[] = [];
  selectedPlaylistIds: number[] = [];
  selectedContentIsPaidAddOn: boolean = false;
  @Input() groupId: number = 0;

  // Cache options to prevent re-calculation
  private _recurrenceOptions = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  private _recurrenceEndOptions = [
    { value: 'never', label: 'Never' },
    // { value: 'count', label: 'After' },
    { value: 'until', label: 'On date' }
  ];

  private _reminderOptions = [
    { value: 5, label: '5 minutes before' },
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 120, label: '2 hours before' },
    { value: 1440, label: '1 day before' }
  ];

  private _weekDayOptions = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' }
  ];
    @ViewChild('drawer') drawer!: NzDrawerComponent;
    timeOptions: SupportTimeOptions = {
      nzFormat: 'hh:mm a',
      nzUse12Hours: true
    }
  constructor(
    private fb: FormBuilder,
    private userMediaAppService: UserMediaAppService,
    private displayListService: DisplayListService,
    private message: NzMessageService,
    private appUtils: AppUtils,
    private planAccess: PlanAccessService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Content loading is now handled by the media drawer component
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset media selection when drawer becomes visible (fresh start)
    if (changes['visible'] && changes['visible'].currentValue === true && 
        changes['visible'].previousValue === false) {
      this.resetMediaSelection();
      // Set default name for new events (not in edit mode)
      if (!this.isEditMode) {
        this.eventForm.patchValue({ name: this.generateDefaultEventName() });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.eventForm = this.fb.group({
      // Backend model fields
      id: [null], // For editing existing events
      schedulerId: [0, Validators.required],
      name: [this.generateDefaultEventName(), [Validators.required, Validators.maxLength(100)]],
      description: [''],
      eventType: [1, Validators.required], // 1 = normal event, 2 = recurring, etc.
      cronExpression: [''],
      cronExpressionProperties: [''],
      timeZone: ['UTC'],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      repeatEndDate: [null],
      userMediaAppId: [0, Validators.required],
      displayListId: [0],
      userId: [''],
      contentType: ['media'], // media or playlist selection - sent to backend
      contentName: [''], // Display name of selected content - sent to backend
      
      // UI helper fields (not sent to backend)
      content: [null, Validators.required],
      color: ['#1890ff'],
      isRecurring: [false],
      recurrenceType: ['none'], // none, daily, weekly, monthly, yearly
      recurrenceInterval: [1],
      recurrenceEndType: ['never'], // never, count, until
      recurrenceCount: [1],
      recurrenceUntil: [null],
      weeklyDays: [[]],
      hasReminder: [false],
      reminderType: ['notification'], // notification, email
      reminderTime: [15], // minutes before event
      location: [''],
      priority: ['medium'], // low, medium, high
      allDay: [false]
    });

    // Watch for content type changes
    this.eventForm.get('contentType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(contentType => {
        this.selectedContentType = contentType;
        // Clear selected content when type changes
        this.eventForm.patchValue({ 
          content: null,
          contentName: ''
        });
        // Reset selected media
        this.resetMediaSelection();
      });
  }

  private autoFillNameFromContent(contentName: string): void {
    if (!contentName) return;
    const nameControl = this.eventForm.get('name');
    if (!nameControl) return;

    const currentName = (nameControl.value || '').trim();
    const defaultPrefix = 'Event - ';
    nameControl.setValue(contentName);
  }



  onContentTypeChange(contentType: string): void {
    this.selectedContentType = contentType;
    // Clear selected content when type changes
    this.eventForm.patchValue({ 
      content: null,
      contentName: ''
    });
    // Reset selected media
    this.resetMediaSelection();
  }



  onClose(): void {
    // Reset media selection and edit mode when drawer closes
    this.resetMediaSelection();
    this.isEditMode = false;
    this.close.emit();
  }

  onSave(): void {
    // Auto-generate name if empty to prevent data loss
    const nameControl = this.eventForm.get('name');
    if (!nameControl?.value || nameControl.value.trim() === '') {
      nameControl?.setValue(this.generateDefaultEventName());
    }
    
    // Mark all fields as touched to show validation errors
    this.markFormGroupTouched(this.eventForm);
    
    if (this.eventForm.valid) {
      this.isSaving = true;
      const eventData = this.transformToBackendModel();
      this.save.emit(eventData);
    }
  }

  // Method to reset loading state (should be called from parent after save operation)
  resetSavingState(): void {
    this.isSaving = false;
  }
  
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
  
  public generateDefaultEventName(): string {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `Event - ${dateStr}`;
  }

  private transformToBackendModel(): any {
    const formValue = this.eventForm.value;
    
    // Generate cron expression and properties based on recurrence settings
    const cronData = this.generateCronExpression(formValue);
    
    // Use helper fields directly - they already contain what the user selected
    let userMediaAppId = formValue.contentType === 'media' ? formValue.content : 0;
    let displayListId = formValue.contentType === 'playlist' ? formValue.content : 0;
    
    const backendModel: any = {
      schedulerId: formValue.schedulerId || 0,
      name: formValue.name,
      description: formValue.description || '',
      eventType: formValue.isRecurring ? 2 : 1, // 1 = single event, 2 = recurring
      cronExpression: cronData.cronExpression,
      cronExpressionProperties: JSON.stringify(cronData.properties),
      timeZone: formValue.timeZone || this.defaultTimeZone || 'UTC',
      startDate: formValue.startDate ? new Date(formValue.startDate).toISOString() : null,
      endDate: formValue.endDate ? new Date(formValue.endDate).toISOString() : null,
      repeatEndDate: formValue.repeatEndDate ? new Date(formValue.repeatEndDate).toISOString() : null,
      userMediaAppId: userMediaAppId,
      userId: formValue.userId || '',
      priority: formValue.priority || 'medium',
    };

    // Expose isPaidAddOn at the top level so the parent can apply the subscription gate
    backendModel.isPaidAddOn = this.selectedContentIsPaidAddOn;

    // Include ID for updates
    if (this.isEditMode && formValue.id) {
      backendModel.id = formValue.id;
    }
    
    // Only add displayListId if it's a playlist, otherwise backend uses userMediaAppId
    if (formValue.contentType === 'playlist') {
      backendModel.displayListId = displayListId;
    }
    
    return backendModel;
  }

  public generateCronExpression(formValue: any): { cronExpression: string; properties: any } {
    const startDate = new Date(formValue.startDate);
    const endDate = formValue.endDate ? new Date(formValue.endDate) : null;
    
    // Always use UTC for cron expression generation
    const utcStartDate = new Date(startDate.toISOString());
    
    // Convert to machine timezone for cron scheduling
    const machineTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const machineStartDate = new Date(startDate.toLocaleString("en-US", {timeZone: machineTimeZone}));
    

    // Simple properties using helper fields directly
    const properties = {
      startDate: startDate.toISOString(),
      endDate: endDate ? endDate.toISOString() : null,
      // timeZone: 'UTC', // Always store as UTC
      machineTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZone: formValue.timeZone || this.defaultTimeZone || 'UTC',
      allDay: formValue.allDay || false,
      priority: formValue.priority || 'medium',
      color: formValue.color || '#1890ff',
      location: formValue.location || '',
      contentType: formValue.contentType || 'media',
      contentName: formValue.contentName || '',
      isPaidAddOn: this.selectedContentIsPaidAddOn,
      yearInterval: formValue.recurrenceYearInterval || 1,
      reminder: {
        enabled: formValue.hasReminder || false,
        type: formValue.reminderType || 'notification',
        time: formValue.reminderTime || 15
      },
      type: formValue.isRecurring ? formValue.recurrenceType : 'once',
      interval: formValue.recurrenceInterval || 1,
      endType: formValue.recurrenceEndType || 'never',
      count: formValue.recurrenceCount || 1,
      until: formValue.recurrenceUntil ? new Date(formValue.recurrenceUntil).toISOString() : null,
      weeklyDays: formValue.weeklyDays || []
    };
    
    // Use UTC for cron expression timing
    // For all-day events, use * for minute and hour (no specific time)
    const isAllDay = formValue.allDay || false;
    const minute = isAllDay ? '*' : utcStartDate.getUTCMinutes();
    const hour = isAllDay ? '*' : utcStartDate.getUTCHours();
    const dayOfMonth = utcStartDate.getUTCDate();
    const month = utcStartDate.getUTCMonth() + 1;
    const dayOfWeek = startDate.getDay();
    let cronExpression = '';
    if (formValue.isRecurring) {
    // Generate cron expression based on recurrence frequency
    // For recurring events, startDate/endDate define the recurrence period boundaries
    // The cron expression defines the frequency pattern within that period
    switch (formValue.recurrenceType) {
      case 'daily':
        // Daily: "30 14 * * *" or "* * * * *" for all-day
        // Active only between startDate and endDate (or until/count limits)
        if (properties.interval > 1) {
          // For intervals like "every 2 days", use */2 syntax if supported
          cronExpression = `${minute} ${hour} */${properties.interval} * *`;
        } else {
          cronExpression = `${minute} ${hour} * * *`;
        }
        
        // Don't automatically set until date for 'never' - let it run indefinitely
        break;
        
      case 'weekly':
        // Weekly: "30 14 * * 1,3,5" or "* * * * 1,3,5" for all-day
        // Active only between startDate and endDate (recurrence boundary)
        const weeklyDays = formValue.weeklyDays && formValue.weeklyDays.length > 0 
          ? formValue.weeklyDays.join(',')
          : dayOfWeek.toString();
        cronExpression = `${minute} ${hour} * * ${weeklyDays}`;
        properties.weeklyDays = formValue.weeklyDays || [dayOfWeek];
        
        // Don't automatically set until date for 'never' - let it run indefinitely
        break;
        
      case 'monthly':
        // Monthly: "30 14 15 * *" or "* * 15 * *" for all-day
        // Active only between startDate and endDate (recurrence boundary)
        if (properties.interval > 1) {
          cronExpression = `${minute} ${hour} ${dayOfMonth} */${properties.interval} *`;
        } else {
          cronExpression = `${minute} ${hour} ${dayOfMonth} * *`;
        }
        
        // Don't automatically set until date for 'never' - let it run indefinitely
        break;
        
      case 'yearly':
        // Yearly: "30 14 15 6 *" or "* * 15 6 *" for all-day
        // Active only between startDate and endDate (recurrence boundary)
        if (properties.interval > 1) {
          properties.yearInterval = properties.interval;
        }
        cronExpression = `${minute} ${hour} ${dayOfMonth} ${month} *`;
        
        // Don't automatically set until date for 'never' - let it run indefinitely
        break;
        
      default:
        cronExpression = '';
    }
    } else {
      // One-time event: "30 14 15 6 *" or "* * 15 6 *" for all-day
      cronExpression = `${minute} ${hour} ${dayOfMonth} ${month} *`;
    }

    return { cronExpression, properties };
  }



  private parseBackendDateTime(dateTimeString: string | null, timeZone?: string): Date | null {
    if (!dateTimeString) return null;
    
    // Parse the backend date string and convert to machine timezone
    const date = new Date(dateTimeString);
    const machineTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Convert to machine timezone for display
    const machineLocalDate = new Date(date.toLocaleString("en-US", {timeZone: machineTimeZone}));
    
    return machineLocalDate;
  }



  setEventData(data: any): void {
    // Set edit mode if we have an ID
    this.isEditMode = !!(data && data.id);
    
    // Transform and patch the form with the event data
    if (data) {
      // Parse cronExpressionProperties to get additional metadata
      let cronProperties: any = {};
      try {
        if (data.cronExpressionProperties && typeof data.cronExpressionProperties === 'string') {
          cronProperties = JSON.parse(data.cronExpressionProperties);
        } else if (data.cronExpressionProperties && typeof data.cronExpressionProperties === 'object') {
          cronProperties = data.cronExpressionProperties;
        }
      } catch (e) {
        console.warn('Failed to parse cronExpressionProperties:', e);
        cronProperties = {};
      }

      // Get timezone from data or properties, with fallback
      const eventTimeZone = cronProperties.timeZone || data.timeZone || this.defaultTimeZone || 'UTC';
      
      // Map the incoming data to form field names - format dates properly
      const formData = {
        id: data.id || null,
        schedulerId: data.schedulerId || this.schedulerId,
        name: data.title || data.name || '',
        description: data.description || '',
        eventType: data.isRecurring ? 2 : 1,
        cronExpression: data.cronExpression || '',
        cronExpressionProperties: data.cronExpressionProperties || '',
        timeZone: eventTimeZone,
        startDate: data.startDate ? this.parseBackendDateTime(data.startDate, eventTimeZone) : null,
        endDate: data.endDate ? this.parseBackendDateTime(data.endDate, eventTimeZone) : null,
        userMediaAppId: data.contentType === 'media' ? (data.contentId || 0) : 0,
        displayListId: data.contentType === 'playlist' ? (data.contentId || 0) : 0,
        userId: data.userId || this.userId,
        
        // UI helper fields - get contentType and contentName from cronProperties first
        contentType: cronProperties.contentType || data.contentType || 'media',
        content: data.contentId || null,
        contentName: cronProperties.contentName || data.contentName || '',
        // isPaidAddOn is not a form field but we restore it from cron properties for the gate check
        color: cronProperties.color || data.color || '#1890ff',
        
        // Determine if this is a recurring event based on eventType or cronProperties
        isRecurring: data.eventType === 2 || (cronProperties.type && cronProperties.type !== 'once' && cronProperties.type !== 'none'),
        recurrenceType: (cronProperties.type && cronProperties.type !== 'once') ? cronProperties.type : (data.recurrenceType && data.recurrenceType !== 'none' ? data.recurrenceType : 'none'),
        recurrenceInterval: cronProperties.interval || data.recurrenceInterval || 1,
        recurrenceEndType: cronProperties.endType || data.recurrenceEndType || 'never',
        recurrenceCount: cronProperties.count || data.recurrenceCount || 1,
        recurrenceUntil: cronProperties.until ? this.parseBackendDateTime(cronProperties.until, eventTimeZone) : (data.recurrenceUntil ? this.parseBackendDateTime(data.recurrenceUntil, eventTimeZone) : null),
        weeklyDays: cronProperties.weeklyDays || data.weeklyDays || [],
        hasReminder: !!(cronProperties.reminder?.enabled || (data.reminders && data.reminders.length > 0)),
        reminderType: cronProperties.reminder?.type || (data.reminders && data.reminders[0] ? data.reminders[0].type : 'notification'),
        reminderTime: cronProperties.reminder?.time || (data.reminders && data.reminders[0] ? data.reminders[0].time : 15),
        location: cronProperties.location || data.location || '',
        priority: cronProperties.priority || data.priority || 'medium',
        allDay: cronProperties.allDay || data.allDay || false
      };

      // Restore isPaidAddOn for the subscription gate check (stored in cron properties on save)
      this.selectedContentIsPaidAddOn = cronProperties.isPaidAddOn ?? false;

      console.log('setEventData - cronProperties.type:', cronProperties.type);
      console.log('setEventData - data.eventType:', data.eventType);
      console.log('setEventData - isRecurring will be set to:', data.eventType === 2 || (cronProperties.type && cronProperties.type !== 'once' && cronProperties.type !== 'none'));
      
      this.eventForm.patchValue(formData);
      
      console.log('Form after patching - isRecurring:', this.eventForm.get('isRecurring')?.value);
      console.log('Form after patching - recurrenceType:', this.eventForm.get('recurrenceType')?.value);
      
      // If we have content ID, update content type and populate selection arrays
      if (data.contentId && data.contentType) {
        this.selectedContentType = data.contentType;
        
        // Create mock objects for the selected arrays to enable display
        const contentName = cronProperties.contentName || data.contentName || `Selected ${data.contentType}`;
        const mockContent = { id: data.contentId, name: contentName, title: contentName };
        
        if (data.contentType === 'media') {
          this.selectedMedia = [mockContent];
          this.selectedMediaIds = [data.contentId];
          this.selectedPlaylists = [];
          this.selectedPlaylistIds = [];
        } else if (data.contentType === 'playlist') {
          this.selectedPlaylists = [mockContent];
          this.selectedPlaylistIds = [data.contentId];
          this.selectedMedia = [];
          this.selectedMediaIds = [];
        }
      }
    }
  }



  setSelectedDate(startDate: Date, endDate?: Date, allDay: boolean = false): void {
    // Reset to create mode when setting a new date
    this.isEditMode = false;
    
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(start.getTime() + (60 * 60 * 1000)); // Default 1 hour duration
    
    // Use the default timezone for new events
    const timeZone = this.defaultTimeZone || 'UTC';
    
    // Reset form to default values for new event
    this.eventForm.reset();
    this.eventForm.patchValue({
      id: null,
      schedulerId: this.schedulerId,
      name: this.generateDefaultEventName(),
      description: '',
      eventType: 1,
      cronExpression: '',
      cronExpressionProperties: '',
      timeZone: timeZone,
      startDate: start,
      endDate: end,
      userMediaAppId: 0,
      displayListId: 0,
      userId: this.userId,
      contentType: 'media',
      content: null,
      contentName: '',
      color: '#1890ff',
      isRecurring: false,
      recurrenceType: 'none',
      recurrenceInterval: 1,
      recurrenceEndType: 'never',
      recurrenceCount: 1,
      recurrenceUntil: null,
      weeklyDays: [],
      hasReminder: false,
      reminderType: 'notification',
      reminderTime: 15,
      location: '',
      priority: 'medium',
      allDay: allDay
    });
  }

  onRecurrenceToggle(): void {
    const isRecurring = this.eventForm.get('isRecurring')?.value;
    if (!isRecurring) {
      // Reset all recurrence fields when disabled
      this.eventForm.patchValue({
        recurrenceType: 'none',
        recurrenceInterval: 1,
        recurrenceEndType: 'never',
        recurrenceCount: 1,
        recurrenceUntil: null,
        weeklyDays: []
      });
    } else {
      // Set default values when enabled
      this.eventForm.patchValue({
        recurrenceType: 'daily',
        recurrenceInterval: 1,
        recurrenceEndType: 'never'
      });
    }
  }

  onRecurrenceTypeChange(): void {
    const recurrenceType = this.eventForm.get('recurrenceType')?.value;
    
    // Reset specific recurrence fields based on type
    if (recurrenceType !== 'weekly') {
      this.eventForm.patchValue({ weeklyDays: [] });
    }
    
    // Set default interval based on type
    const defaultIntervals: { [key: string]: number } = {
      'daily': 1,
      'weekly': 1,
      'monthly': 1,
      'yearly': 1
    };
    
    if (defaultIntervals[recurrenceType]) {
      this.eventForm.patchValue({ recurrenceInterval: defaultIntervals[recurrenceType] });
    }
  }

  onAllDayToggle(): void {
    const allDay = this.eventForm.get('allDay')?.value;
    if (allDay) {
      const startDate = this.eventForm.get('startDate')?.value;
      
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        
        this.eventForm.patchValue({
          startDate: start,
          endDate: end
        });
      }
    }
  }

  getRecurrenceOptions() {
    return this._recurrenceOptions;
  }

  getReminderOptions() {
    return this._reminderOptions;
  }

  getWeekDayOptions() {
    return this._weekDayOptions;
  }

  getRecurrenceEndOptions() {
    return this._recurrenceEndOptions;
  }

  trackByDay(index: number, item: any): number {
    return item.value;
  }

  trackByEndType(index: number, item: any): string {
    return item.value;
  }

  trackByRecurrence(index: number, item: any): string {
    return item.value;
  }

  isWeekDaySelected(dayValue: number): boolean {
    const weeklyDays = this.eventForm.get('weeklyDays')?.value || [];
    return weeklyDays.includes(dayValue);
  }

  toggleWeekDay(dayValue: number): void {
    const weeklyDaysControl = this.eventForm.get('weeklyDays');
    if (!weeklyDaysControl) return;
    
    let weeklyDays = weeklyDaysControl.value || [];
    
    if (weeklyDays.includes(dayValue)) {
      // Remove the day
      weeklyDays = weeklyDays.filter((day: number) => day !== dayValue);
    } else {
      // Add the day
      weeklyDays = [...weeklyDays, dayValue];
    }
    
    weeklyDaysControl.setValue(weeklyDays);
  }

  getContentTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      'media': 'blue',
      'playlist': 'green',
      'layout': 'orange',
      'image': 'purple'
    };
    return colorMap[type] || 'default';
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }

  // Content drawer methods (for both media and playlist)
  openContentDrawer(): void {
    const limitKey = this.selectedContentType === 'playlist' ? 'max_playlists' : 'max_media_apps';
    if (!this.planAccess.guardLimit(limitKey)) return;
    this.showMedias = true;
    this.drawer?.open();
  }

  // Legacy method for backward compatibility
  openMediaDrawer(): void {
    this.openContentDrawer();
  }

  closeContentDrawer(selectedResult: any): void {
    this.showMedias = false;
    
    if (!selectedResult) return;
    
    // Handle media selection result (already supports single selection)
    if (selectedResult.selectedMedia && selectedResult.selectedMedia.length > 0) {
      // Single selection only - get the first (and only) selected item
      const selectedItem = selectedResult.selectedMedia[0];
      this.selectedMedia = [selectedItem];
      this.selectedMediaIds = [selectedItem.id];
      this.selectedContentIsPaidAddOn = selectedItem.isPaidAddOn ?? false;
      
      // Update form value with selected content ID and name
      const contentId = selectedItem.id;
      const contentName = selectedItem.name || selectedItem.title || 'Selected Media';
      
      // Set the appropriate form fields for media
      const formUpdate: any = {
        content: contentId,
        contentName: contentName,
        userMediaAppId: contentId,
        displayListId: 0
      };
      
      this.eventForm.patchValue(formUpdate);
      this.autoFillNameFromContent(contentName);
    }
  }

  // Legacy method for backward compatibility
  closeMediaDrawer(selectedMediaResult: any): void {
    this.closeContentDrawer(selectedMediaResult);
  }

  // Playlist-specific handler that enforces single selection
  closePlaylistDrawer(selectedPlaylistResult: any): void {
    this.showMedias = false;
    
    if (!selectedPlaylistResult || !selectedPlaylistResult.selectedDisplayLists || selectedPlaylistResult.selectedDisplayLists.length === 0) {
      return;
    }
    
    // Force single selection - only take the first selected playlist
    const selectedItem = selectedPlaylistResult.selectedDisplayLists[0];
    this.selectedPlaylists = [selectedItem];
    this.selectedPlaylistIds = [selectedItem.id];
    this.selectedContentIsPaidAddOn = selectedItem.isPaidAddOn ?? false;
    
    // Update form value with selected playlist
    const contentId = selectedItem.id;
    const contentName = selectedItem.name || selectedItem.title || 'Selected Playlist';
    
    const formUpdate: any = {
      content: contentId,
      contentName: contentName,
      displayListId: contentId,
      userMediaAppId: 0
    };
    
    this.eventForm.patchValue(formUpdate);
    this.autoFillNameFromContent(contentName);
  }

  getSelectedMediaText(): string {
    // In edit mode, show the contentName from the form if available
    const contentName = this.eventForm.get('contentName')?.value;
    if (this.isEditMode && contentName) {
      return contentName;
    }

    // Otherwise, use the selectedMedia array (for new selections) - single selection only
    if (this.selectedMedia && this.selectedMedia.length > 0) {
      const selectedItem = this.selectedMedia[0];
      return selectedItem.name || selectedItem.title || `Selected ${this.selectedContentType}`;
    }
    return `Select ${this.selectedContentType} content`;
  }

  resetMediaSelection(): void {
    // Reset all content-related properties (works for both media and playlist)
    this.selectedMedia = [];
    this.selectedMediaIds = [];
    this.selectedPlaylists = [];
    this.selectedPlaylistIds = [];
    this.selectedContentIsPaidAddOn = false;
    this.showMedias = false;
    
    // Reset form values related to content selection
    this.eventForm.patchValue({
      content: null,
      contentName: '',
      userMediaAppId: 0,
      displayListId: 0
    });
  }

  getCurrentGroupId(): number {
    return this.groupId ?? 0;
  }

  getSelectedContentText(): string {
    const contentName = this.eventForm.get('contentName')?.value;
    const contentType = this.eventForm.get('contentType')?.value;
    
    // In edit mode, prioritize the contentName from the form
    if (this.isEditMode && contentName) {
      return contentName;
    }
    
    // For new selections, check the appropriate selected arrays
    if (contentType === 'media') {
      if (this.selectedMedia && this.selectedMedia.length > 0) {
        const selectedItem = this.selectedMedia[0];
        return selectedItem.name || selectedItem.title || 'Selected Media';
      }
    } else if (contentType === 'playlist') {
      if (this.selectedPlaylists && this.selectedPlaylists.length > 0) {
        const selectedItem = this.selectedPlaylists[0];
        return selectedItem.name || selectedItem.title || 'Selected Playlist';
      }
    }
    
    // Fallback to contentName if available
    if (contentName) {
      return contentName;
    }
    
    return `Select ${contentType}`;
  }
}
